"use client";

/**
 * Client body for /use-cases/engineering — the bug & code demo page. Copy in
 * the `ucEngineering` namespace (NL lead, EN lockstep). The full demo video is
 * embedded through <VideoBlock>, which renders an honest "being finished"
 * note until the final deliverable is published in the video manifest.
 *
 * The "never straight to the main branch" claim on this page is backed by the
 * product's PR/MR-only delivery path (see CLAUDE.md "External writes") — do
 * not soften or extend it without re-verifying that behavior.
 */

import {
  Bug,
  FileSearch,
  FlaskConical,
  FolderKanban,
  GitPullRequest,
  PenLine,
  Scale,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  Faq,
  UseCaseHero,
  Section,
  SectionHeader,
  WorkflowSteps,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { VideoBlock } from "@/components/marketing/video/video-block";
import { useT } from "@/i18n";

export function EngineeringContent() {
  const t = useT();
  const k = (s: string) => `ucEngineering.${s}`;

  const steps = [
    { key: "s1", icon: <Bug size={18} /> },
    { key: "s2", icon: <FolderKanban size={18} /> },
    { key: "s3", icon: <FileSearch size={18} /> },
    { key: "s4", icon: <PenLine size={18} /> },
    { key: "s5", icon: <FlaskConical size={18} /> },
    { key: "s6", icon: <Scale size={18} /> },
    { key: "s7", icon: <UserCheck size={18} /> },
    { key: "s8", icon: <GitPullRequest size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`how.steps.${s.key}.tag`)),
    title: t(k(`how.steps.${s.key}.title`)),
    body: t(k(`how.steps.${s.key}.body`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "#video", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section id="video">
        <SectionHeader
          eyebrow={t(k("video.eyebrow"))}
          title={t(k("video.title"))}
          description={t(k("video.description"))}
        />
        <div className="mt-10">
          <VideoBlock id="fixcontrol-engineering-nl" posterCaption={t(k("video.title"))} />
        </div>
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("review.eyebrow"))} title={t(k("review.title"))} />
        <SecurityNote icon={<ShieldCheck size={18} />} title={t(k("review.title"))}>
          {t(k("review.body"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq
          items={["q1", "q2", "q3", "q4"].map((q) => ({
            q: t(k(`faq.${q}.q`)),
            a: t(k(`faq.${q}.a`)),
          }))}
        />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/support",
              label: t(k("related.support.label")),
              description: t(k("related.support.description")),
            },
            {
              href: "/use-cases/support-to-engineering-remediation",
              label: t(k("related.remediation.label")),
              description: t(k("related.remediation.description")),
            },
            {
              href: "/integrations/github",
              label: t(k("related.github.label")),
              description: t(k("related.github.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
